import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { useSession } from '@/lib/hooks/useSession';
import { useTenantStore } from '@/stores/tenantStore';
import { supabase } from '@/integrations/supabase/client';
import { API_BASE } from '@/lib/apiBase';

const toErrorMessage = (value: unknown, fallback: string): string => {
  if (value instanceof Error && typeof value.message === 'string') {
    return value.message;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

interface SocketState {
  socket: Socket | null;
  connected: boolean;
  error: string | null;
}

type ViteEnv = {
  VITE_SOCKET_URL?: string;
  VITE_REALTIME_URL?: string;
  DEV?: boolean;
};

const readEnvString = (key: keyof ViteEnv): string | undefined => {
  if (typeof import.meta === 'undefined' || typeof import.meta.env !== 'object') {
    return undefined;
  }
  const envRecord = import.meta.env as Record<string, unknown>;
  const value = envRecord[key as string];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const isDevEnv = (): boolean => {
  if (typeof import.meta === 'undefined' || typeof import.meta.env !== 'object') {
    return false;
  }
  const envRecord = import.meta.env as Record<string, unknown>;
  const flag = envRecord.DEV;
  if (typeof flag === 'boolean') {
    return flag;
  }
  if (typeof flag === 'string') {
    return flag.toLowerCase() === 'true';
  }
  return Boolean(flag);
};

export const useSocket = (): SocketState => {
  const { data: sessionData } = useSession();
  const { activeRestaurantId } = useTenantStore();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionReady = sessionData?.state === 'OK' && Boolean(sessionData?.authenticated);
  const lastTenantRef = useRef<string | null>(null);

  const resolveSocketUrl = (): string | null => {
    const envUrl = readEnvString('VITE_SOCKET_URL') ?? readEnvString('VITE_REALTIME_URL');
    if (envUrl) {
      return envUrl;
    }

    if (API_BASE && /^https?:\/\//i.test(API_BASE)) {
      try {
        const apiUrl = new URL(API_BASE);
        return apiUrl.origin;
      } catch (parseError) {
        console.warn('[useSocket] Failed to parse API_BASE for realtime URL:', parseError);
      }
    }

    if (isDevEnv()) {
      return 'http://localhost:5000';
    }

    if (typeof window !== 'undefined' && typeof window.location?.origin === 'string') {
      return window.location.origin;
    }

    return null;
  };

  useEffect(() => {
    let cancelled = false;

    const connect = async (): Promise<void> => {
      if (!sessionReady || !activeRestaurantId) {
        return;
      }

      if (socketRef.current && socketRef.current.connected && lastTenantRef.current === activeRestaurantId) {
        return;
      }

      let accessToken: string | null = null;
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        const session = data?.session;
        accessToken = typeof session?.access_token === 'string' ? session.access_token : null;
      } catch (err) {
        console.error('[useSocket] Failed to load Supabase session:', err);
        if (!cancelled) {
          setError(toErrorMessage(err, 'Failed to load session'));
          setConnected(false);
        }
        return;
      }

      if (!accessToken) {
        if (!cancelled) {
          setError('Missing Supabase access token');
          setConnected(false);
        }
        return;
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      lastTenantRef.current = null;

      const socketUrl = resolveSocketUrl();
      if (!socketUrl) {
        if (!cancelled) {
          console.error('[useSocket] Unable to determine realtime socket URL');
          setError('Realtime service unavailable');
          setConnected(false);
        }
        return;
      }

      const socket = io(socketUrl, {
        auth: { token: accessToken },
        transports: ['websocket', 'polling'],
        withCredentials: true,
      });

      socketRef.current = socket;
      lastTenantRef.current = activeRestaurantId;

      socket.on('connect', () => {
        if (cancelled) {
          return;
        }
        console.log('[useSocket] Connected to server');
        setConnected(true);
        setError(null);
        socket.emit('tenant.join', { tenantId: activeRestaurantId });
      });

      socket.on('disconnect', (reason: string) => {
        if (cancelled) {
          return;
        }
        console.log('[useSocket] Disconnected from server:', reason);
        setConnected(false);
      });

      socket.on('connect_error', (err: Error) => {
        if (cancelled) {
          return;
        }
        console.error('[useSocket] Connection error:', err);
        setError(err.message ?? 'Connection error');
        setConnected(false);
      });

      socket.on('error', (err: unknown) => {
        if (cancelled) {
          return;
        }
        console.error('[useSocket] Socket error:', err);
        setError(toErrorMessage(err, 'Socket error'));
      });
    };

    connect().catch((err: unknown) => {
      console.error('[useSocket] Unexpected error during socket connection:', err);
      if (!cancelled) {
        setError(toErrorMessage(err, 'Unexpected connection error'));
        setConnected(false);
      }
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      lastTenantRef.current = null;
    };
  }, [sessionReady, activeRestaurantId]);

  useEffect(() => {
    if (sessionReady && activeRestaurantId) {
      return;
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    lastTenantRef.current = null;
    setConnected(false);
    setError(null);
  }, [sessionReady, activeRestaurantId]);

  return {
    socket: socketRef.current,
    connected,
    error
  };
};
